import {
  Component,
  ChangeDetectionStrategy,
  Input,
  QueryList,
  ContentChildren,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  EventEmitter,
  AfterViewInit,
  Renderer2, Output, Self, Optional, OnInit, HostBinding,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { startWith } from 'rxjs/operators';

import { GasInputBoolean } from '@cikrf/gas-utils/decorators';

import { GasSelectOptionComponent } from '../option/gas-select-option.component';
import { GasSelect } from '../gas-select.namespace';
import { GasFormBaseComponent } from '../../gas-form-base-component';
import { GasForm } from '../../gas-form.namespace';
import { GasBageType } from '../../../layout/gas-badge/gas-badge.interface';
import { GasIcon } from '../../../utils/gas-icons/icon/gas-icon.namespace';
import { GasColor } from '../../../core/colors/color.namespace';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

/**
 * @description Селектор
 *
 * @example
 * <gas-select [label]="label$ | async" [tip]="tip$ | async" [error]="error$ | async" [info]="info" [success]="success">
 *   <select gasSelect [required]="required" [disabled]="disabled" [formControl]="form">
 *     <option value="1">Please select</option>
 *     <option value="2">Option One</option>
 *     <option value="3">Option Two</option>
 *     <option value="4">Option Three</option>
 *   </select>
 * </gas-select>
 */
@UntilDestroy()
@Component({
  selector: 'gas-select',
  templateUrl: './gas-select.component.html',
  styleUrls: ['./gas-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GasSelectComponent extends GasFormBaseComponent implements OnInit, AfterViewInit {
  @Input()
  public set label(labelText: string) {
    this.labelSubject$.next(labelText);
  }

  public get label(): string {
    return this.labelSubject$.getValue();
  }

  public get placeholder(): string {
    return this.nativePlaceholder !== null && this.nativePlaceholder.length > 0
      ? this.nativePlaceholder
      : (this.nativePlaceholder === null ? 'Выберите значение' : '');
  }

  @Input()
  public set tip(tipText: string) {
    this.tipSubject$.next(tipText);
  }

  @Input('placeholder')
  public nativePlaceholder: string | null = null;

  @GasInputBoolean()
  @Input()
  public multi: string | boolean = false;

  @GasInputBoolean()
  @Input('search')
  public isSearch: string | boolean;

  /** Скрывает блочную структуру у label и tip/error, по умолчанию место под них остается, чтоб при появлении они не двигали блок */
  @GasInputBoolean()
  @Input()
  public hideAll: string | boolean = false;

  @GasInputBoolean()
  @Input()
  public loading: string | boolean;

  @Input()
  public selectType: 'default' | 'icon' = 'default';

  @Input()
  public clearable = false;

  @Input()
  public selectIcon: GasIcon.Icons | string = 'arrows-chevron-down';

  @Input()
  public selectIconColor: GasColor.IconColorClass | string = GasColor.IconColorClass.White;

  @Input()
  public onlySelected = false;

  @Input()
  public error = '';

  @Output()
  public allSelected = new EventEmitter<boolean>();

  @ContentChildren(GasSelectOptionComponent)
  public options: QueryList<GasSelectOptionComponent>;

  @ViewChild('fieldRef')
  public fieldRef: ElementRef<HTMLElement>;

  @ViewChild('valueRef')
  public valueRef: ElementRef<HTMLElement>;

  public activateEmit$ = new EventEmitter<string | number>();
  public closeEmit$ = new EventEmitter<void>();
  public resetEmit$ = new EventEmitter<void>();

  public get selectedTitle(): string {
    return this.getTitle(this.form.value);
  }

  public get badgeTheme(): GasBageType {
    if (this.state === GasForm.InputState.Primary) {
      return 'inverse';
    } else if (GasForm.InputState.Success) {
      return 'success';
    } else if (GasForm.InputState.Error) {
      return 'error';
    } else {
      return 'shade';
    }
  }

  public isNeedLabel = false;

  public tip$: Observable<string>;
  public label$: Observable<string>;

  public get mappedOptions(): GasSelect.Option[] {
    return this.options?.map((option: GasSelectOptionComponent) => {
      const gasOption: GasSelect.Option = {
        title: option.title,
        value: option.value,
      };

      return gasOption;
    });
  }

  public get activeOptions(): string[] | number[] {
    return Array.isArray(this.form.value)
      ? this.form.value
      : this.form.value === null ? [] : [this.form.value];
  }

  public get allOptionsActive(): boolean {
    return this.options.length === this.activeOptions.length;
  }

  public isOpenedDropdown = false;

  public changeSubject$ = new Subject<void>();

  private tipSubject$ = new BehaviorSubject<string>('');
  private labelSubject$ = new BehaviorSubject<string>('');

  private breakpointIndex = 0;


  @HostBinding('class._error')
  public get showError(): boolean {
    const showError = Boolean(!this.hideAll && this.error);

    if (!this.ngControl) {
      return showError;
    }
    return this.ngControl?.control?.dirty && this.ngControl?.control?.invalid && showError;
  }

  constructor(
    public changeDetectorRef: ChangeDetectorRef,
    public elementRef: ElementRef,
    public renderer: Renderer2,
    @Self() @Optional() public ngControl: NgControl,
  ) {
    super(changeDetectorRef, elementRef, renderer);

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    this.tip$ = this.tipSubject$.asObservable();
    this.label$ = this.labelSubject$.asObservable();

    this.activateEmit$
      .pipe(
        untilDestroyed(this),
      )
      .subscribe((value: string | number) => {
        this.writeValue(value);

        if (!this.multi) {
          this.closeEmit$.emit();
        } else {
          this.initOptions();
        }
      });

    this.resetEmit$
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.form.reset();

        this.closeEmit$.emit();
      });
  }

  public ngOnInit(): void {
    /**TODO: перенести данную реализацию в GasFormBaseComponent и перевести на нее все компоненты */
    this.ngControl?.control?.statusChanges.pipe(
      startWith(this.ngControl.control.status),
      untilDestroyed(this),
    ).subscribe((status: GasForm.States) => {
      this.nativeDisabled = status === GasForm.States.Disabled;
      /**
       * необходимо для обновления состояния, если селект находится во вложенности компонентов
       *  и формконтрол идет от родителя к дочернему компоненту
       */
      this.changeDetectorRef.markForCheck();
    });
  }

  public ngAfterViewInit(): void {
    this.changeDetectorRef.detectChanges();

    this.options.changes.pipe(
      untilDestroyed(this),
    )
      .subscribe(() => {
        this.changeSubject$.next();
        this.changeDetectorRef.detectChanges();
      });
  }

  public hasValue(): boolean {
    return this.options?.length
      && this.form.value !== undefined
      && this.form.value !== null
      && (!Array.isArray(this.form.value) || this.form.value.length > 0);
  }

  public writeValue(value: unknown): void {
    /** Проверяем, multi select или нет */
    if (this.multi) {
      let currentValues: unknown[] = Array.isArray(this.form.value) ? this.form.value : [];

      const selectedValues: string[] | number[] = Array.isArray(value)
        ? value
        : (value ? [value] : []);

      if (selectedValues.length === 0) {
        currentValues = [];
      }

      /** Ходим по полученным значениям */
      selectedValues.forEach((selectedValue: any) => {
        /** И если одно из полученных значений имеется в текущей форме - выключаем его(toggle) */
        const found = currentValues.indexOf(selectedValue) !== -1;

        if (found) {
          currentValues = currentValues.filter((innerValue: any) => innerValue !== selectedValue);
        }
        /** Если нет этого значения, то добавляем его */
        else {
          currentValues.push(selectedValue);
        }
      });

      this.form.setValue(JSON.parse(JSON.stringify(currentValues)));
    }
    /** В случае одиночного выбора, просто перезаписываем */
    else {
      this.form.setValue(value);
    }

    this.changeDetectorRef.detectChanges();
  }

  public getTitle(value: string | number): string {
    const resultTitle = this.options.find((option: GasSelectOptionComponent) => option.value === value)?.title || '';

    return this.allOptionsActive && this.multi ? 'Все' : resultTitle;
  }

  private initOptions(): void {
    if (this.valueRef) {
      const arrowOffset = 40;
      const fieldWidth: number = this.fieldRef.nativeElement.offsetWidth - arrowOffset;
      const valueWidth: number = this.valueRef.nativeElement.offsetWidth;

      const isLabel = valueWidth > fieldWidth;
      const countValues = this.form.value.length;

      if (!this.breakpointIndex || countValues < this.breakpointIndex) {
        this.isNeedLabel = isLabel;
        this.breakpointIndex = 0;
      }

      if (isLabel) {
        this.breakpointIndex = countValues;
      }
    }

    if (!this.changeDetectorRef['destroyed']) {
      this.changeDetectorRef.detectChanges();
    }
  }
}
